import {
	type MeResponse,
	type PlaceKind,
	type TransportRequestSource,
	TRANSPORT_NAME_MAX,
	TRANSPORT_PASSENGERS_MAX,
	TRANSPORT_PHONE_MAX,
	TRANSPORT_REMARKS_MAX,
	TRANSPORT_SETTINGS_RANGES,
	TRANSPORT_UNNAMED_MAX,
	type TransportPlaceOption,
	isAdminRole,
	makeCreateTransportRequest,
} from "@seagames/contracts";
import { Field, Icons, Modal, ModalActions, Select, type SelectOption, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../lib/api/client";
import { accreditationListOptions } from "../../lib/api/accreditations";
import { createTrip, invalidateTrips, placeOptionsOptions, transportSettingsOptions } from "../../lib/api/transport-fleet";
import { formatClockIn, formatDateTime, formatDayShort, wallTimeIn, wallTimeToIso, zoneAbbreviation } from "../../lib/format/datetime";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";
import { addDays, todayIn } from "../../lib/travel/view";
import { toast } from "../../lib/ui/toast";
import { inputClass, useProblemText } from "../transport-fleet/form-bits";
import { useTransportFormMessages as useTripFormMessages } from "../../lib/i18n/use-form-schemas";
import { type Problems, hasProblems, issuesToProblems } from "../transport-fleet/model";
import { WheelchairIcon } from "../transport/icons";
import { DateInput } from "../common/date-input";

type Field_ = "org" | "date" | "time" | "from" | "to" | "count" | "wheelchair" | "named" | "return_time" | "contact_name" | "contact_phone" | "remarks";
const FIELDS: readonly Field_[] = ["org", "date", "time", "from", "to", "count", "wheelchair", "named", "return_time", "contact_name", "contact_phone", "remarks"];
/** Contract paths → this sheet's fields. */
const RENAME: Partial<Record<string, Field_>> = {
	responsible_organization_id: "org",
	pickup_at: "time",
	return_pickup_at: "return_time",
	unnamed_count: "count",
	wheelchair_unnamed: "wheelchair",
	ac_card_ids: "named",
};

/** A named passenger, as the sheet lists them. */
export interface Named {
	ac_card_id: string;
	name: string;
	card: string;
}

/** A place as the sheet's selects hold it (`ENTRY_POINT:<id>`, `VENUE:<id>`…). */
export const placeValue = (p: { kind: PlaceKind; id: string }) => `${p.kind}:${p.id}`;
const parsePlace = (v: string): { kind: PlaceKind; id: string } | null => {
	const [kind, id] = v.split(":");
	return kind && id ? { kind: kind as PlaceKind, id } : null;
};

/** The desk's three kinds of trip (`mode="desk"`): a venue trip, or a transfer from / to an entry point. */
const DESK_KINDS = ["ADHOC", "ARR", "DEP"] as const satisfies readonly TransportRequestSource[];
export type DeskTripKind = (typeof DESK_KINDS)[number];

/** What the sheet opens with (the logistics desk's "Arrange transfer": the kind, the people, their time and entry point). */
export interface TripPrefill {
	kind?: DeskTripKind;
	org?: string;
	date?: string;
	/** `HH:MM` on the games' clock. */
	time?: string;
	from?: string;
	to?: string;
	named?: Named[];
}

/**
 * Request a trip (her NOC `tripSheet`, PLAN §4 / X16): a venue trip for
 * training or competition — date and pick-up time on the games' clock,
 * from / to (venues with a cluster, transport places, the contingent's own
 * drop-off first), passengers as a head count and/or named APPROVED records,
 * wheelchair users, an optional return, the contact on the day (the driver
 * and the transport company call this person) and remarks. It goes straight
 * to the desk (no draft). For an ORGANIZATION login the booking lead
 * (`noc_lead_hours`, 12 h) is HARD: the API refuses earlier (422
 * TRIP_LEAD_TIME), and the sheet says so before sending.
 *
 * `mode="desk"` (the dispatch Overview's "New trip", GMS-2): the same sheet
 * with a Kind — Venue trip / Arrival transfer (from an entry point) /
 * Departure transfer (to one). A transfer's named passengers follow their
 * own leg of that direction (`legs_linked`), so they leave the suggestions;
 * one already on another transfer rides without it (the toast says so).
 */
export function RequestTripModal({
	session,
	gamesId,
	timeZone,
	onClose,
	mode = "request",
	initialDate,
	prefill,
}: {
	session: Pick<MeResponse, "active_role" | "group">;
	gamesId: string;
	timeZone: string;
	onClose: () => void;
	mode?: "request" | "desk";
	/** The day the sheet opens on (the Overview's picked day); default today, or tomorrow for a NOC. */
	initialDate?: string | undefined;
	/** Desk only: the sheet filled in (still checked and sent by the person). */
	prefill?: TripPrefill | undefined;
}) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const problemText = useProblemText();
	const messages = useTripFormMessages();
	const zone = zoneAbbreviation(timeZone);
	const today = todayIn(timeZone);
	const isOrganization = session.active_role === "ORGANIZATION";
	const places = useQuery(placeOptionsOptions(gamesId));
	const settings = useQuery({ ...transportSettingsOptions(gamesId), retry: false });
	const leadHours = settings.data?.noc_lead_hours ?? TRANSPORT_SETTINGS_RANGES.noc_lead_hours.default;
	const orgChoice = useOrgChoice(session);

	const desk = mode === "desk";
	const [kind, setKind] = useState<DeskTripKind>(desk ? (prefill?.kind ?? "ADHOC") : "ADHOC");
	const ownDropoff = places.data?.find((p) => p.own_dropoff && p.kind !== "ENTRY_POINT" && p.cluster);
	const [draft, setDraft] = useState({
		org: orgChoice.fixed ?? prefill?.org ?? "",
		date: prefill?.date && prefill.date >= today ? prefill.date : initialDate && initialDate >= today ? initialDate : isOrganization ? addDays(today, 1) : today,
		time: prefill?.time ?? "",
		from: prefill?.from ?? "",
		to: prefill?.to ?? "",
		count: "",
		wheelchair: "0",
		return_time: "",
		contact_name: "",
		contact_phone: "",
		remarks: "",
	});
	const [named, setNamed] = useState<Named[]>(prefill?.named ?? []);
	const [showNamed, setShowNamed] = useState(false);
	// A transfer is about people: their legs link only when they are named.
	const namedOpen = showNamed || kind !== "ADHOC";
	const [problems, setProblems] = useState<Problems<Field_>>({});
	const [busy, setBusy] = useState(false);
	const firstField = useRef<HTMLInputElement>(null);
	// The contingent's drop-off pre-fills "Pick up at" once the options arrive (her "(your rooms)") — on a venue trip only.
	const from = draft.from || (kind === "ADHOC" && ownDropoff ? placeValue(ownDropoff) : "");

	const set = (k: keyof typeof draft, v: string) => {
		setDraft((d) => ({ ...d, [k]: v }));
		setProblems((p) => ({ ...p, [k]: undefined }));
	};

	const placeOptions = useMemo<SelectOption[]>(() => {
		const usable = (places.data ?? []).filter((p): p is TransportPlaceOption & { cluster: NonNullable<TransportPlaceOption["cluster"]> } => p.kind !== "ENTRY_POINT" && p.cluster !== null);
		const group = (p: TransportPlaceOption) => (p.own_dropoff ? t("request.group.dropoff") : p.kind === "PLACE" ? t("request.group.places") : t("request.group.venues"));
		return [...usable]
			.sort((a, b) => Number(b.own_dropoff) - Number(a.own_dropoff) || (a.kind === b.kind ? 0 : a.kind === "PLACE" ? -1 : 1) || a.name.localeCompare(b.name))
			.map((p) => ({ value: placeValue(p), label: p.name, description: `${p.cluster.code} · ${p.cluster.name}`, group: group(p) }));
	}, [places.data, t]);
	// A transfer's entry-point end (airports, ports, land crossings).
	const entryOptions = useMemo<SelectOption[]>(
		() =>
			(places.data ?? [])
				.filter((p) => p.kind === "ENTRY_POINT")
				.sort((a, b) => a.name.localeCompare(b.name))
				.map((p) => ({ value: placeValue(p), label: p.code ? `${p.code} · ${p.name}` : p.name, description: p.cluster ? `${p.cluster.code} · ${p.cluster.name}` : undefined, group: t("request.group.entryPoints") })),
		[places.data, t],
	);
	const fromOptions = kind === "ARR" ? entryOptions : placeOptions;
	const toOptions = kind === "DEP" ? entryOptions : placeOptions;
	const pickKind = (k: (typeof DESK_KINDS)[number]) => {
		setKind(k);
		// The ends change meaning: start them over.
		setDraft((d) => ({ ...d, from: "", to: "", return_time: "" }));
		setProblems({});
	};

	const pickupIso = draft.date && draft.time ? wallTimeToIso(draft.date, draft.time, timeZone) : null;
	const minPickup = isOrganization ? new Date(Date.now() + leadHours * 3_600_000) : null;

	const submit = async (e: React.FormEvent) => {
		e.preventDefault();
		const found: Problems<Field_> = {};
		if (orgChoice.required && !draft.org) found.org = { key: "orgRequired" };
		if (!draft.date) found.date = { key: "dateRequired" };
		if (!draft.time) found.time = { key: "timeRequired" };
		else if (pickupIso && Date.parse(pickupIso) < Date.now()) found.time = { key: "pickupPassed" };
		else if (pickupIso && minPickup && Date.parse(pickupIso) < minPickup.getTime())
			found.time = { key: "leadTime", values: { hours: formatNumber(leadHours), when: whenLabel(minPickup.toISOString(), timeZone) } };
		if (!from) found.from = { key: "placeRequired" };
		if (!draft.to) found.to = { key: "placeRequired" };
		const count = draft.count.trim() === "" ? 0 : Number(draft.count);
		const wheelchair = draft.wheelchair.trim() === "" ? 0 : Number(draft.wheelchair);
		if (!Number.isInteger(count) || count < 0 || count > TRANSPORT_UNNAMED_MAX) found.count = { key: "countInvalid" };
		else if (count + named.length < 1) found.count = { key: "passengersRequired" };
		if (!Number.isInteger(wheelchair) || wheelchair < 0 || wheelchair > count) found.wheelchair = { key: "wheelchairTooMany" };
		setProblems(found);
		if (hasProblems(found) || !pickupIso) return;

		let returnIso: string | null = null;
		if (draft.return_time && kind === "ADHOC") {
			returnIso = wallTimeToIso(draft.date, draft.return_time, timeZone);
			// A return at or before the pick-up is the next morning (hers: `(ret − time) mod 1440`).
			if (returnIso && Date.parse(returnIso) <= Date.parse(pickupIso)) returnIso = wallTimeToIso(addDays(draft.date, 1), draft.return_time, timeZone);
		}
		const fromPlace = parsePlace(from);
		const toPlace = parsePlace(draft.to);
		const parsed = makeCreateTransportRequest(messages).safeParse({
			games_id: gamesId,
			source: kind,
			responsible_organization_id: draft.org || null,
			from: fromPlace,
			to: toPlace,
			pickup_at: pickupIso,
			return_pickup_at: returnIso,
			ac_card_ids: named.map((n) => n.ac_card_id),
			unnamed_count: count,
			wheelchair_unnamed: wheelchair,
			contact_name: draft.contact_name,
			contact_phone: draft.contact_phone,
			remarks: draft.remarks,
		});
		if (!parsed.success) {
			setProblems(issuesToProblems(parsed.error.issues, FIELDS, "remarks", RENAME));
			return;
		}
		setBusy(true);
		try {
			const created = await createTrip(parsed.data);
			await invalidateTrips();
			if (kind === "ADHOC") toast.success(desk ? t("request.created", { ref: created.ref }) : t("request.sent", { ref: created.ref }));
			else toast.success(t("request.createdTransfer", { ref: created.ref, linked: t("request.legsLinked", { count: created.legs_linked, n: formatNumber(created.legs_linked) }) }));
			if (created.already_on_transfer.length > 0)
				toast.info(
					t("request.alreadyOnTransfer", {
						count: created.already_on_transfer.length,
						n: formatNumber(created.already_on_transfer.length),
						refs: [...new Set(created.already_on_transfer.map((x) => x.ref))].join(", "),
					}),
				);
			onClose();
		} catch (err) {
			if (err instanceof ApiError && err.code === "TRIP_LEAD_TIME") {
				const min = typeof err.details?.["min_pickup_at"] === "string" ? (err.details["min_pickup_at"] as string) : null;
				setProblems({ time: { key: "leadTime", values: { hours: formatNumber(leadHours), when: min ? whenLabel(min, timeZone) : "—" } } });
			} else if (err instanceof ApiError && Object.keys(err.fieldErrors).length > 0) {
				setProblems(issuesToProblems(Object.keys(err.fieldErrors).map((k) => ({ path: [k], message: t("validation.invalidValue") })), FIELDS, "remarks", RENAME));
			} else {
				toast.error(err, t("request.failed"));
			}
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={desk ? t("request.deskTitle") : t("request.title")}
			description={desk ? t("request.deskDescription") : t("request.description")}
			dismissible={!busy}
			initialFocusRef={firstField}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
						{tc("action.cancel")}
					</button>
					<button type="submit" form="request-trip-form" className="btn btn-primary btn-sm" disabled={busy}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{desk ? t("request.create") : t("request.send")}
					</button>
				</ModalActions>
			}
		>
			<form id="request-trip-form" onSubmit={(e) => void submit(e)} noValidate className="grid gap-[0.75rem] sm:grid-cols-2">
				{desk && (
					<fieldset className="sm:col-span-2">
						<legend className="mb-[0.375rem] text-[0.8125rem] font-semibold text-gms-navy-ink">{t("request.kind.label")}</legend>
						<div role="radiogroup" aria-label={t("request.kind.label")} className="flex flex-wrap gap-[0.125rem] rounded-[0.5rem] bg-gms-soft-2 p-[0.1875rem]">
							{DESK_KINDS.map((k) => (
								<button
									key={k}
									type="button"
									role="radio"
									aria-checked={kind === k}
									onClick={() => pickKind(k)}
									className={`h-[2rem] flex-1 cursor-pointer rounded-[0.375rem] px-[0.75rem] text-[0.8125rem] font-semibold whitespace-nowrap transition-colors ${
										kind === k ? "bg-base-100 text-gms-navy shadow-[0_0_0_1px_var(--color-gms-line)]" : "text-gms-ink-2 hover:text-gms-navy"
									}`}
								>
									{t(`request.kind.${k}`)}
								</button>
							))}
						</div>
						{kind !== "ADHOC" && <p className="mt-[0.375rem] text-[0.75rem] text-gms-ink-3">{kind === "ARR" ? t("request.kind.arrHint") : t("request.kind.depHint")}</p>}
					</fieldset>
				)}
				{orgChoice.options && (
					<Field label={t("request.org")} htmlFor="rt-org" required={orgChoice.required} error={problemText(problems.org)} className="sm:col-span-2">
						<Select id="rt-org" value={draft.org || (orgChoice.required ? null : "")} onChange={(v) => set("org", v ?? "")} options={orgChoice.options} placeholder={t("request.orgPlaceholder")} />
					</Field>
				)}

				<Field label={t("request.date")} htmlFor="rt-date" required error={problemText(problems.date)}>
					<DateInput ref={firstField} id="rt-date" min={today} value={draft.date} onChange={(v) => set("date", v)} className={inputClass(!!problems.date, "tabular-nums")} />
				</Field>
				<Field
					label={t("request.time", { zone })}
					htmlFor="rt-time"
					required
					error={problemText(problems.time)}
					hint={isOrganization ? t("request.leadHint", { count: leadHours, n: formatNumber(leadHours) }) : t("request.timeHint", { zone })}
				>
					<input id="rt-time" type="time" value={draft.time} onChange={(e) => set("time", e.target.value)} className={inputClass(!!problems.time, "tabular-nums")} />
				</Field>

				<Field label={t("request.from")} htmlFor="rt-from" required error={problemText(problems.from)} className="sm:col-span-2">
					<Select id="rt-from" value={from || null} onChange={(v) => set("from", v ?? "")} options={fromOptions} placeholder={places.isPending ? tc("state.loading") : t("request.choose")} />
				</Field>
				<Field label={t("request.to")} htmlFor="rt-to" required error={problemText(problems.to)} className="sm:col-span-2">
					<Select id="rt-to" value={draft.to || null} onChange={(v) => set("to", v ?? "")} options={toOptions.filter((o) => o.value !== from)} placeholder={places.isPending ? tc("state.loading") : t("request.choose")} />
				</Field>

				<Field label={t("request.count")} htmlFor="rt-count" required={named.length === 0} error={problemText(problems.count)} hint={named.length > 0 ? t("request.countWithNamed", { count: named.length, n: formatNumber(named.length) }) : undefined}>
					<input id="rt-count" type="number" inputMode="numeric" min={0} max={TRANSPORT_UNNAMED_MAX} value={draft.count} onChange={(e) => set("count", e.target.value)} className={inputClass(!!problems.count, "tabular-nums")} />
				</Field>
				<Field label={t("request.wheelchair")} htmlFor="rt-wheel" error={problemText(problems.wheelchair)}>
					<label className={inputClass(!!problems.wheelchair)}>
						<WheelchairIcon className="size-[0.9375rem] text-gms-ink-3" />
						<input id="rt-wheel" type="number" inputMode="numeric" min={0} value={draft.wheelchair} onChange={(e) => set("wheelchair", e.target.value)} className="grow tabular-nums" />
					</label>
				</Field>

				<div className="sm:col-span-2">
					{namedOpen ? (
						<NamedPassengers gamesId={gamesId} orgId={draft.org} picked={named} onChange={setNamed} error={problemText(problems.named)} />
					) : (
						<button type="button" className="inline-flex cursor-pointer items-center gap-[0.375rem] text-[0.8125rem] font-semibold text-gms-navy hover:underline" onClick={() => setShowNamed(true)}>
							<Icons.Plus className="size-[0.875rem]" />
							{t("request.addNamed")}
						</button>
					)}
				</div>

				{kind === "ADHOC" && (
					<Field label={t("request.return", { zone })} htmlFor="rt-return" aside={tc("state.optional")} error={problemText(problems.return_time)} hint={t("request.returnHint")} className="sm:col-span-2">
						<input id="rt-return" type="time" value={draft.return_time} onChange={(e) => set("return_time", e.target.value)} className={inputClass(!!problems.return_time, "tabular-nums sm:max-w-[12rem]")} />
					</Field>
				)}

				<Field label={t("request.contactName")} htmlFor="rt-contact" aside={tc("state.optional")} error={problemText(problems.contact_name)} hint={t("request.contactHint")}>
					<input id="rt-contact" value={draft.contact_name} onChange={(e) => set("contact_name", e.target.value)} maxLength={TRANSPORT_NAME_MAX} autoComplete="off" className={inputClass(!!problems.contact_name)} />
				</Field>
				<Field label={t("request.contactPhone")} htmlFor="rt-phone" aside={tc("state.optional")} error={problemText(problems.contact_phone)}>
					<input id="rt-phone" type="tel" inputMode="tel" value={draft.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} maxLength={TRANSPORT_PHONE_MAX} placeholder="+66 81 234 5678" className={inputClass(!!problems.contact_phone, "tabular-nums")} />
				</Field>

				<Field label={t("request.remarks")} htmlFor="rt-remarks" aside={tc("state.optional")} error={problemText(problems.remarks)} className="sm:col-span-2">
					<input id="rt-remarks" value={draft.remarks} onChange={(e) => set("remarks", e.target.value)} maxLength={TRANSPORT_REMARKS_MAX} autoComplete="off" placeholder={t("request.remarksPlaceholder")} className={inputClass(!!problems.remarks)} />
				</Field>
			</form>
		</Modal>
	);
}

/** "Tue 14 Oct, 9:30AM MYT" — when the lead time allows the first pick-up. */
function whenLabel(iso: string, timeZone: string): string {
	const wall = wallTimeIn(iso, timeZone);
	return wall ? `${formatDayShort(wall.date)}, ${formatClockIn(iso, timeZone)} ${zoneAbbreviation(timeZone)}` : formatDateTime(iso);
}

/**
 * Which organisation the trip is for. A NOC login's group names it in its
 * scope rows: one organisation → fixed, never asked; several → a choice
 * among them; a scope row for "any organisation" (or an admin) → every
 * organisation. The host (OPERATION) may also book its own trip (none).
 * The API re-checks the organisation against the group's writable scope.
 */
function useOrgChoice(session: Pick<MeResponse, "active_role" | "group">): { fixed: string | null; options: SelectOption[] | null; required: boolean } {
	const { t } = useTranslation("transportFleet");
	const isOrganization = session.active_role === "ORGANIZATION";
	const rows = session.group?.scopes.filter((s) => s.can_write) ?? [];
	const anyOrg = isAdminRole(session.active_role) || rows.some((s) => s.responsible_organization === null);
	const named = [...new Map(rows.filter((s) => s.responsible_organization).map((s) => [s.responsible_organization!.id, s.responsible_organization!.name])).entries()];
	const all = useQuery({ ...responsibleOrganizationOptionsOptions(), enabled: anyOrg });

	const host = isOrganization ? [] : [{ value: "", label: t("request.hostTrip") }];
	if (!anyOrg && named.length === 1 && isOrganization) return { fixed: named[0]![0], options: null, required: true };
	const list = anyOrg ? (all.data ?? []).map((o) => ({ value: o.responsible_organization_id, label: o.name })) : named.map(([id, name]) => ({ value: id, label: name }));
	return { fixed: null, options: [...host, ...list.sort((a, b) => a.label.localeCompare(b.label))], required: isOrganization };
}

/**
 * Name the passengers (optional): search APPROVED records of the contingent
 * (search on Enter, never per keystroke), tick to add, × to remove. At most
 * TRANSPORT_PASSENGERS_MAX. Their wheelchair needs come from their records.
 */
function NamedPassengers({ gamesId, orgId, picked, onChange, error }: { gamesId: string; orgId: string; picked: Named[]; onChange: (n: Named[]) => void; error?: string }) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber } = useUiStrings();
	const [draft, setDraft] = useState("");
	const [q, setQ] = useState<string | null>(null);
	const results = useQuery({
		...accreditationListOptions({ games_id: gamesId, status: "APPROVED", responsible_organization_id: orgId || undefined, q: q ?? undefined, limit: 20 }),
		enabled: q !== null,
	});
	const has = new Set(picked.map((p) => p.ac_card_id));
	const full = picked.length >= TRANSPORT_PASSENGERS_MAX;

	return (
		<fieldset className="grid gap-[0.5rem] rounded-field border border-gms-line bg-gms-soft px-[0.75rem] py-[0.625rem]">
			<legend className="px-[0.25rem] text-[0.8125rem] font-semibold text-gms-navy-ink">
				{t("request.namedTitle")} <span className="font-normal text-gms-ink-3 tabular-nums">{formatNumber(picked.length)}</span>
			</legend>
			{/* Not a <form>: it sits inside the sheet's form. Enter or the button searches. */}
			<label htmlFor="rt-named-q" className="text-[0.75rem] font-medium text-gms-ink-2">
				{t("request.namedSearch")}
			</label>
			<div role="search" className="-mt-[0.25rem] flex gap-[0.5rem]">
				<input
					id="rt-named-q"
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							setQ(draft.trim());
						}
					}}
					className="input input-sm min-w-0 flex-1"
				/>
				<button type="button" className="btn btn-line btn-sm" onClick={() => setQ(draft.trim())}>
					{t("request.namedFind")}
				</button>
			</div>
			{error && <p className="text-[0.78125rem] font-medium text-gms-bad">{error}</p>}
			{picked.length > 0 && (
				<ul className="flex flex-wrap gap-[0.375rem]">
					{picked.map((p) => (
						<li key={p.ac_card_id} className="inline-flex items-center gap-[0.25rem] rounded-[0.375rem] bg-base-100 py-[0.125rem] pr-[0.25rem] pl-[0.5rem] text-[0.78125rem] text-gms-navy-ink shadow-[0_0_0_1px_var(--color-gms-line)]">
							{p.name}
							<button
								type="button"
								aria-label={t("request.namedRemove", { name: p.name })}
								onClick={() => onChange(picked.filter((x) => x.ac_card_id !== p.ac_card_id))}
								className="grid size-[1.25rem] cursor-pointer place-items-center rounded-full text-gms-ink-3 hover:bg-gms-soft-2 hover:text-gms-navy-ink"
							>
								<Icons.Close className="size-[0.75rem]" />
							</button>
						</li>
					))}
				</ul>
			)}
			{q !== null &&
				(results.isPending ? (
					<p className="text-[0.78125rem] text-gms-ink-3">{t("common.loading")}</p>
				) : results.isError ? (
					<p className="text-[0.78125rem] text-gms-bad">{t("request.namedFailed")}</p>
				) : (results.data?.items.length ?? 0) === 0 ? (
					<p className="text-[0.78125rem] text-gms-ink-3">{t("request.namedNone")}</p>
				) : (
					<ul className="grid max-h-[12rem] gap-[0.125rem] overflow-y-auto rounded-[0.375rem] bg-base-100 p-[0.25rem] shadow-[0_0_0_1px_var(--color-gms-line)]">
						{results.data?.items.map((r) => {
							const on = has.has(r.ac_card_id);
							return (
								<li key={r.ac_card_id}>
									<label className={`flex cursor-pointer items-center gap-[0.5rem] rounded-[0.25rem] px-[0.375rem] py-[0.25rem] text-[0.8125rem] hover:bg-gms-soft ${full && !on ? "opacity-50" : ""}`}>
										<input
											type="checkbox"
											className="checkbox checkbox-xs checkbox-primary"
											checked={on}
											disabled={full && !on}
											onChange={(e) =>
												onChange(
													e.target.checked
														? [...picked, { ac_card_id: r.ac_card_id, name: r.person.printed_name, card: r.accreditation_no_display ?? r.registration_no_display }]
														: picked.filter((x) => x.ac_card_id !== r.ac_card_id),
												)
											}
										/>
										<span className="min-w-0 flex-1 truncate font-medium text-gms-navy-ink">{r.person.printed_name}</span>
										<span className="shrink-0 text-[0.75rem] text-gms-ink-3 tabular-nums">{r.accreditation_no_display ?? r.registration_no_display}</span>
									</label>
								</li>
							);
						})}
					</ul>
				))}
		</fieldset>
	);
}
